<template>
  <base-block-tmp
    class="article-block"
    :size="size"
    :layout-type="layoutType"
    :url="url"
    type="article"
    @simulateClick="simulateClickOnLink"
  >
    <template #media>
      <ica-tag
        v-if="labelText"
        size="small"
        class="article-block--tag coupon-tag"
      >
        <template #content>
          {{ labelText }}
        </template>
      </ica-tag>
      <epi-pic
        v-edit="'Image'"
        :image="image"
        :sizes="dimensions"
        :sharpen="80"
        auto-quality="best"
        :use-picture="false"
        :auto-aspect-ratio="false"
        :manual-dpr="$root.isSmallViewport ? 1.25 : null"
      />

      <epi-property
        id="Image"
        text="Bild"
      />
    </template>
    <template #content>
      <div
        v-edit="'Prefix'"
        class="article-block__content__prefix"
      >
        {{ prefix }}
      </div>
      <h3
        v-edit="'Title'"
        aria-label="Artikel"
        class="article-block__content__title"
      >
        <component
          :is="(url && !buttonLabel) ? 'a' : 'span'"
          :ref="(url && !buttonLabel) ? 'articleBlockLink' : 'notUsed'"
          :href="(url && !buttonLabel) ? url : null"
          :aria-label="(url && !buttonLabel) ? title : null"
          :class="(url && !buttonLabel) ? 'no-link-look' : ''"
          @click.stop="(url && !buttonLabel) ? trackClick() : null"
        >
          {{ title }}
        </component>
      </h3>
      <div
        v-edit="'Preamble'"
        class="article-block__content__preamble"
      >
        {{ preamble }}
      </div>
      <a
        v-if="(url && buttonLabel) || isEditable"
        :ref="(url && buttonLabel) ? 'articleBlockLink' : 'notUsed'"
        v-edit="'ButtonLabel'"
        :href="url"
        :aria-label="(url && buttonLabel) ? buttonLabel : null"
        class="button--secondary article-block__content__button"
        :class="buttonClass"
        @click.stop="trackClick()"
        v-text="buttonLabel"
      />
      <epi-property
        v-if="buttonLabel"
        id="url"
        text="Knapplänk"
      />
    </template>
  </base-block-tmp>
</template>

<script>
import BaseBlockTmp, { layoutTypes } from './BaseBlockComponent'
import EpiPic from '../common/picture/epiPicture.vue'

import { IcaTag } from '~/@ica/ica-molecules-vue/src/components'
import ga from '../../../analytics/dataLayer'

export default {
  name: "ArticleBlock",
  components: {
    BaseBlockTmp,
    IcaTag,
    EpiPic
  },
  props: {
    blockSize: {
      type: String,
      default: 'quarter',
      validator (value) {
        return ['full', 'half', 'third', 'quarter'].includes(value)
      }
    },
    buttonLabel: {
      type: String,
      default: null
    },
    prefix: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    preamble: {
      type: String,
      default: ''
    },
    image: {
      type: Object,
      default: null
    },
    url: {
      type: String,
      default: null
    },
    labelText: {
      type: String,
      default: ''
    },
    tracking: {
      type: Object,
      default: function () {
        return {}
      }
    },
  },
  data () {
    return {
      dimensions: {
        small: {
          width: 767
        },
        medium: {
          width: 480
        },
        large: {
          width: 570
        }
      },
    }
  },
  computed: {
    size () {
      return this.blockSize
    },
    isSize (){
      return {
        [this.size]: true
      }
    },
    isHighlight () {
      return ['full', 'half'].includes(this.size)
    },
    layoutType () {
      switch(this.$root.currentSize) {
        case 'sm':
        case 'xs':
        case 'xxs':
          return this.isHighlight ? layoutTypes.mediaTop : layoutTypes.mediaRightSmall
        case 'md':
        case 'lg':
        case 'xl':
          return this.isSize.full ? layoutTypes.mediaLeft : layoutTypes.mediaTop
      }

      return layoutTypes.mediaTop
    },
    buttonClass (){
      return this.$root.isLargeViewport && this.isSize.full ? 'button--48' : 'button--40'
    }
  },
  methods: {
    simulateClickOnLink () {
      this.$refs.articleBlockLink.click()
    },
    trackClick () {
      ga.add({
        event: 'articleSelect',
        linkName: this.title,
        linkUrl: !/^https?:\/\//i.test(this.url) ? window.location.origin + this.url : this.url,
        ...this.tracking
      })
    }
  }
}
</script>

<style lang="scss">
.article-block {
  .button--editbutton {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }

  .base-block-tmp__media {
    position: relative;
    order: 3;

    .epi-picture {
      padding: 0.5rem 0.5rem 0.5rem 0;
    }
  }

  .base-block-tmp__media-lg {
    position: relative;

    .epi-picture {
      padding: 0;
    }
  }

  &.base-block-tmp--quarter,
  &.base-block-tmp--third {
    .base-block-tmp__content {
      text-align: left;
    }
  }

  &__content {
    &__prefix {
      .base-block-tmp.base-block-tmp--half .base-block-tmp__content & {
        @include font(hand, 1, "normal");
      }
    }

    &__title {
      .base-block-tmp .base-block-tmp__content & {
        @include font(rubrik, 2, "mid");
      }

      .base-block-tmp.base-block-tmp--full .base-block-tmp__content & {
        @include font(rubrik, 3, "mid");
      }
    }

    &__preamble {
      .base-block-tmp .base-block-tmp__content & {
        margin-top: 0.5rem;
      }

      @include media-breakpoint-up(lg) {
        .base-block-tmp--half .base-block-tmp__content & {
          margin-top: 0.5rem;
        }
      }

      @include media-breakpoint-up(lg) {
        .base-block-tmp--full .base-block-tmp__content & {
          margin-top: 1.5rem;
        }
      }

      @include media-breakpoint-down(md) {
        .base-block-tmp--full .base-block-tmp__content & {
          margin-top: 1rem;
        }
      }

      @include media-breakpoint-down(sm) {
        .base-block-tmp--full .base-block-tmp__content & {
          margin-top: 0.5rem;
        }
      }
    }

    &__button {
      &.button--48 {
        margin-top: 2.5rem;
      }

      .base-block-tmp--highlight & {
        @include media-breakpoint-only(md) {
          margin-top: 2rem;
        }
      }

      margin-top: 1rem;
    }
  }

  @include media-breakpoint-down(sm) {
    &.base-block-tmp--highlight .base-block-tmp__inner-wrapper {
      min-height: 30rem;

      .base-block-tmp__media-lg {
        display: flex;
        flex-direction: column;
        min-height: 12rem;

        .epi-picture {
          display: flex;
          flex-direction: column;
          flex: 1 1 0%;

          div {
            flex: 1 1 0%;
            padding-bottom: 63%;
          }
        }
      }
    }
  }

  $contentPadding: '.base-block-tmp__content .base-block-tmp__content__padding';
  // sm & md highlight padding
  &.base-block-tmp.base-block-tmp--highlight #{$contentPadding} {
    @include media-breakpoint-down(sm) {
      padding: 1.5rem 1.5rem 2rem 1.5rem;
    }

    @include media-breakpoint-up(md) {
      padding: 3.5rem 3rem;
    }
  }

  // sm & md normal padding
  &.base-block-tmp #{$contentPadding} {
    @include media-breakpoint-down(md) {
      padding: 1.5rem 2rem 1.5rem 1.5rem;
    }

    @include media-breakpoint-down(sm) {
      padding: 1.5rem 1rem;
    }
  }

  // xl padding
  @include media-breakpoint-up(xl) {
    &.base-block-tmp.base-block-tmp--quarter #{$contentPadding},
    &.base-block-tmp.base-block-tmp--third #{$contentPadding} {
      padding: 1.5rem 1rem 2rem;
    }
  }

  &--tag {
    &.tag-wrapper {
      position: absolute;
      z-index: 1;
      left: 0;
      top: 0.8rem;

      .tags {
        background: $color-pink-special;
        color: $color-pink-dark;
      }

      .angle-right path {
        fill: $color-pink-special;
      }
    }
  }
}
</style>
